<script lang="ts">
  import type { FacetValue, FacetValueOptions } from "./type.ts";
  import { Text } from "../facet-value-label";
  import { featureTrackingAttributes, removeStatusIf } from "../tracking";
  import { preselected } from "../facet/preselection.svelte.ts";
  import type { ClassValue } from "svelte/elements";

  interface Props extends FacetValue, FacetValueOptions {
    class?: ClassValue;
    excludeFromForm?: boolean;
    selectedValues?: string[];
    variantGray?: boolean;
    fillParent?: boolean;
    showRemoveIcon?: boolean;
  }

  let {
    id, /*                                        */
    children = text,
    selection = "multi",
    saved = false,
    featureTracking,
    "aria-label": ariaLabel,
    checked,
    popular,
    nba,
    itemCount,
    referencedFacetValueId,
    excludeFromForm,
    variantGray,
    fillParent = false,
    showRemoveIcon = false,
    selectedValues = $bindable(),
    ...rest
  }: Props = $props();

  /*                     */
  const tracking = $derived(removeStatusIf(!checked && !popular, featureTracking));
  let dataChecked = $state(!!checked);

  /*                            */
  ariaLabel ||= rest.forceAriaLabel ? rest.title : undefined;

  /*                   */
  let dangerousItemCountClass = itemCount ? "" : "heureka_facetValue__dangerousZeroHits";

  function onchange({ currentTarget }: Event & { currentTarget: HTMLInputElement }) {
    /*                                        */
    dataChecked = currentTarget.checked;
    selectedValues = currentTarget.checked
      ? [...(selectedValues || []), currentTarget.value]
      : selectedValues?.filter((v) => v !== currentTarget.value);
  }

  function optionalAttribute<T>(value: T): T | undefined {
    if (value) {
      return value;
    } else {
      return undefined;
    }
  }

  const shouldBeSelected = $derived.by(() => {
    if (!id) return checked;

    if (Object.keys($preselected).includes(id)) {
      return $preselected[id];
    } else {
      return checked;
    }
  });

  const inputProps = $derived({
    "aria-label": "oc-auto",
    name: excludeFromForm ? undefined : rest.name,
    value: rest.value,
    checked: optionalAttribute(shouldBeSelected),
    "data-default-checked": !!checked,
    "data-ts-feature-ref": referencedFacetValueId,
  });
</script>

{#snippet text()}
  <Text size="125" {...{ ...rest, showCount: false }} bold={!!checked} />
{/snippet}

<oc-chip-v3
  {id}
  class={[
    "hc_chip hc_facetValueInput js_hc_tValue",
    excludeFromForm && "hc_facetValueInput--noSubmission",
    variantGray && "hc_chip--always-gray",
    dangerousItemCountClass,
    rest.class,
  ]}
  class:hc_chip--always-gray={variantGray}
  width-behavior={fillParent ? "fill" : "fit"}
  icon-right={showRemoveIcon ? "clear" : undefined}
  icon-left={saved ? "wishlist-active" : undefined}
  data-checked={dataChecked}
  data-default-checked={!!checked}
  aria-label={ariaLabel}
  {...featureTrackingAttributes(tracking)}
  data-ts-feature-ref={referencedFacetValueId}
  {...nba ? { "data-nba": true } : {}}
>
  {#if selection === "single"}
    <input type="radio" {...inputProps} {onchange} />
  {:else if selection === "multi"}
    <input type="checkbox" {...inputProps} {onchange} />
  {/if}
  <button slot="content">{@render children?.()}</button>
</oc-chip-v3>

<style lang="scss">
  .hc_chip {
    --checked-font-weight: bold;
    &--always-gray {
      --checked-font-weight: normal;
    }

    min-width: 59px;

    /*                               */
    @at-root :global(.hc_chip[data-checked="true"] .hc_label) {
      font-weight: var(--checked-font-weight);
    }

    @at-root :global(.hc_chip[data-checked="true"] .hc_color .hc_color__title) {
      font-weight: var(--checked-font-weight);
    }
  }
</style>
